import React, { useState, useEffect } from 'react';
import { crmAPI } from '../../lib/api';
import { Plus, Edit, Trash2, Search, RefreshCw } from 'lucide-react';

const LiftBrands = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        status: 'active'
    });

    useEffect(() => { fetchBrands(); }, []);

    const fetchBrands = async () => {
        try {
            setLoading(true);
            const response = await crmAPI.getLiftBrands();
            setBrands(response.data.data?.data || response.data.data || []);
        } catch (err) { 
            setError('Failed to load brands'); 
        }
        finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) await crmAPI.updateLiftBrand(editingId, formData);
            else await crmAPI.createLiftBrand(formData);
            setShowModal(false);
            resetForm();
            fetchBrands();
        } catch (err) { 
            setError(err.response?.data?.message || 'Failed to save'); 
        }
    };

    const handleEdit = (brand) => {
        setFormData({
            name: brand.name || '',
            code: brand.code || '',
            description: brand.description || '',
            status: brand.status || 'active'
        });
        setEditingId(brand.id);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try { 
            await crmAPI.deleteLiftBrand(id); 
            fetchBrands(); 
        }
        catch (err) { 
            setError('Failed to delete'); 
        }
    };

    const resetForm = () => { 
        setFormData({ name: '', code: '', description: '', status: 'active' }); 
        setEditingId(null); 
    };

    const filteredBrands = brands.filter(b => 
        b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Lift Brands</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage lift brands (Beacon, Orbis, Alikraft, etc.)</p>
                </div>
                <button onClick={() => { resetForm(); setShowModal(true); }} className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                    <Plus className="w-4 h-4 mr-2" /> Add Brand
                </button>
            </div>
            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}
            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Search brands..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" />
            </div>
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Code</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {filteredBrands.length > 0 ? filteredBrands.map(brand => (
                                    <tr key={brand.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200 font-medium">{brand.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{brand.code || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{brand.description || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${brand.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                {brand.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button onClick={() => handleEdit(brand)} className="text-indigo-600 hover:text-indigo-900 mr-3"><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => handleDelete(brand.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No brands found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">{editingId ? 'Edit' : 'Add'} Brand</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name <span className="text-red-500">*</span></label>
                                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code</label>
                                <input type="text" value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" placeholder="e.g., BCN for Beacon" />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" rows="2" />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                                <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200">
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                            <div className="flex justify-end gap-3 mt-4">
                                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">{editingId ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiftBrands;
